/* The Draft Report panel — the working audit report, in the same section order
   as the downloaded PDF.

   Two kinds of content, and the panel makes the difference visible:
     • generated — AI/derived, read-only. Composed by the same backend function
       the PDF renders from, so what you read here is what prints.
     • editable — analyst-authored. Blank until you fill it in; saved back to the
       project and printed verbatim by the PDF.

   Everything the analyst types lives in one `editable` object with an explicit
   Save. No autosave: a half-typed action plan must not reach the PDF. */
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  generateDraftReport,
  getDraftReport,
  saveDraftReport,
  type DraftEditable,
  type DraftFinding,
  type DraftGenerated,
} from '../../services/api';
import { FormattedText } from '../FormattedText';
import { AuditRatingGauge } from './AuditRatingGauge';
import { RootCauseDonut } from './RootCauseDonut';
import {
  APPENDIX_A_ROWS,
  APPENDIX_B_NOTES,
  APPENDIX_B_ROWS,
} from './appendices';

/* The backend hands us HTML fragments whose prose is LLM-authored. Rendering that
   with innerHTML would make every model response a script-injection surface.

   So: convert the wrapper tags back to markdown, then hand the result to
   FormattedText — the app's markdown-lite renderer, which parses **bold** and
   escapes everything else. Emphasis survives, markup cannot. This also rescues
   drafts generated before the backend converted markdown, whose prose still
   carries literal ** asterisks: FormattedText renders those as bold either way. */
function htmlToMarkdown(html: string): string {
  return html
    .replace(/<\s*(strong|b)\s*>/gi, '**')
    .replace(/<\s*\/\s*(strong|b)\s*>/gi, '**')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    .replace(/<\/?p[^>]*>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/* Backend placeholder strings — from renderer._placeholder AND from
   llm_augment's degraded fallbacks (used when the model is unreachable). They all
   mean "nobody has written this yet", so they must not read as finished content. */
const PLACEHOLDERS = [
  'to be completed',
  'to be filled by management',
  'management to complete',
  'pending management review',
  'under investigation by the responsible team',
  'observation to be captured',
  // llm_augment fallbacks
  'observation detail to be provided by the audit team',
  'consequence to be assessed by management',
  'root cause under review',
  'address the observed control weakness in line with the applicable policy',
  'management action plan to be prepared post-review',
];

function isPlaceholder(text: string): boolean {
  const t = text.trim().toLowerCase();
  return !t || PLACEHOLDERS.some((p) => t.includes(p));
}

function Prose({ html }: { html: string }): JSX.Element {
  const text = htmlToMarkdown(html);
  if (isPlaceholder(text)) {
    return <span className="dr-unfilled">{text || '—'}</span>;
  }
  return <FormattedText text={text} className="dr-prose" />;
}

/* The Contents list, mirroring the AI panel's TOC so both panels navigate the
   same way. Single source of truth: the ids here are the ids the sections render
   with, so a renamed section can't leave a stale link behind. */
const DRAFT_TOC = [
  { id: 'dr-exec', label: '1. Executive Summary' },
  { id: 'dr-rating', label: '2. Audit Rating' },
  { id: 'dr-good', label: '3. Good Practice Noted' },
  { id: 'dr-key', label: '4. Key Findings' },
  { id: 'dr-root', label: '5. Root Cause Analysis' },
  { id: 'dr-summary', label: '6. Summary of Audit Findings' },
  { id: 'dr-objectives', label: '7. The Objectives' },
  { id: 'dr-scope', label: '8. The Scope' },
  { id: 'dr-team', label: '9. Audit Team' },
  { id: 'dr-detailed', label: '10. Detailed Audit Report' },
  { id: 'dr-appendix-a', label: 'Appendix A — Risk Ratings' },
  { id: 'dr-appendix-b', label: 'Appendix B — Rating Matrix' },
];

function Section({
  id,
  title,
  editable,
  children,
}: {
  id: string;
  title: string;
  editable?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="dr-sec" id={id}>
      <h3 className="dr-h">
        {title}
        {editable && <EditableMark />}
      </h3>
      {children}
    </section>
  );
}

/** A section whose content the analyst owns. The badge is the point: it tells the
 *  reader at a glance which parts of this document a human wrote. */
function EditableMark(): JSX.Element {
  return (
    <span
      className="dr-mark"
      data-tip="You write this. The PDF prints exactly what you save here."
    >
      Yours to write
    </span>
  );
}

export function DraftReport({ auditId }: { auditId: string }): JSX.Element {
  const [generated, setGenerated] = useState<DraftGenerated | null>(null);
  const [editable, setEditable] = useState<DraftEditable | null>(null);
  const [saved, setSaved] = useState<string>(''); // serialised last-saved state
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'saving' | 'generating' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeToc, setActiveToc] = useState<string>(DRAFT_TOC[0].id);

  /* Highlight the Contents entry for whatever section is on screen. Same
     approach as the AI panel's TOC; re-binds once the sections exist. */
  useEffect(() => {
    if (loading || !generated) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const top = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (top) setActiveToc(top.target.id);
      },
      { rootMargin: '-25% 0px -60% 0px', threshold: [0, 0.25, 0.5, 1] },
    );
    DRAFT_TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [loading, generated]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDraftReport(auditId)
      .then((d) => {
        if (cancelled) return;
        setGenerated(d.generated);
        setEditable(d.editable);
        setSaved(JSON.stringify(d.editable));
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Failed to load draft report'),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [auditId]);

  const dirty = useMemo(
    () => editable !== null && JSON.stringify(editable) !== saved,
    [editable, saved],
  );

  /* Warn on tab-close with unsaved edits. Losing a hand-written action plan to a
     stray Cmd-W is not recoverable — nothing else on the page is analyst input. */
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const patch = useCallback((fn: (d: DraftEditable) => DraftEditable) => {
    setEditable((cur) => (cur ? fn(cur) : cur));
  }, []);

  const onSave = useCallback(async () => {
    if (!editable) return;
    setBusy('saving');
    setError(null);
    try {
      const next = await saveDraftReport(auditId, editable);
      setEditable(next.editable);
      setSaved(JSON.stringify(next.editable));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(null);
    }
  }, [auditId, editable]);

  const onGenerate = useCallback(async () => {
    setBusy('generating');
    setError(null);
    try {
      const next = await generateDraftReport(auditId);
      setGenerated(next.generated);
      setEditable(next.editable);
      setSaved(JSON.stringify(next.editable));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed');
    } finally {
      setBusy(null);
    }
  }, [auditId]);

  if (loading) {
    return (
      <p className="t-muted" style={{ padding: 'var(--s6)', textAlign: 'center' }}>
        <span className="spinner" /> Loading draft report…
      </p>
    );
  }

  if (error && !editable) {
    return <div className="alert alert-fail">{error}</div>;
  }

  /* No draft yet — a project processed before the draft was generated in-pipeline.
     Offer to build it rather than showing an empty document. */
  if (!generated) {
    return (
      <div className="dr-empty">
        <p className="dr-empty-title">No draft report yet</p>
        <p className="t-muted">
          This project was processed before the draft report was generated
          automatically. Building it runs the AI pass over every finding, which
          takes a few minutes.
        </p>
        <button
          type="button"
          className="btn btn-accent"
          disabled={busy === 'generating'}
          onClick={onGenerate}
        >
          {busy === 'generating' ? 'Generating…' : 'Generate draft report'}
        </button>
        {error && <p className="dr-err">{error}</p>}
      </div>
    );
  }

  const ed = editable as DraftEditable;
  const filledCount =
    (ed.objectives.trim() ? 1 : 0) +
    (ed.scope.length ? 1 : 0) +
    (ed.audit_team.engagement_manager.trim() || ed.audit_team.team_members.length
      ? 1
      : 0) +
    Object.values(ed.findings).filter((r) => r.mgmt_action_plan.trim()).length;
  const toFill = 3 + generated.findings.length;

  return (
    <div className="dr-layout">
      {/* Contents — mirrors the AI panel's TOC so both panels navigate alike. */}
      <aside className="report-toc dr-toc">
        <span className="report-toc-label">Contents</span>
        <nav>
          {DRAFT_TOC.map(({ id, label }) => (
            <a
              key={id}
              href={`#${id}`}
              className={`report-toc-item${activeToc === id ? ' active' : ''}`}
              onClick={() => setActiveToc(id)}
            >
              {label}
            </a>
          ))}
        </nav>
        <div className="dr-toc-foot">
          {/* The bar carries the count on its own now that the caption is gone,
              so it must expose one to assistive tech rather than being hidden. */}
          <div
            className="dr-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={toFill}
            aria-valuenow={filledCount}
            aria-label={`${filledCount} of ${toFill} sections written`}
          >
            <span
              className="dr-progress-fill"
              style={{ width: `${Math.round((filledCount / toFill) * 100)}%` }}
            />
          </div>
        </div>
      </aside>

      <div className="dr">
        {/* Save bar — sticky so the button is reachable from any finding. */}
        <div className="dr-bar">
          <span className={`dr-bar-dot${dirty ? ' dirty' : ''}`} aria-hidden="true" />
          <span className="dr-bar-note">
            {dirty
              ? 'Unsaved changes'
              : `Saved · generated ${new Date(generated.generated_at).toLocaleString()}`}
          </span>
          {error && <span className="dr-err">{error}</span>}
          <button
            type="button"
            className="btn btn-accent btn-sm"
            disabled={!dirty || busy === 'saving'}
            onClick={onSave}
          >
            {busy === 'saving' ? 'Saving…' : 'Save'}
          </button>
        </div>

        <Section id="dr-exec" title="Executive Summary">
          <Prose html={generated.executive_summary} />
        </Section>

        <Section id="dr-rating" title="Audit Rating">
          <AuditRatingGauge rating={generated.audit_rating} />
        </Section>

        <Section id="dr-good" title="Good Practice Noted">
          {generated.good_practice.length === 0 ? (
            <p className="dr-unfilled">No good practice recorded for this deal.</p>
          ) : (
            <ol className="dr-ol">
              {generated.good_practice.map((g, i) => (
                <li key={i}>{g.title}</li>
              ))}
            </ol>
          )}
        </Section>

        <Section id="dr-key" title="Key Findings">
          {generated.key_findings.length === 0 ? (
            <p className="dr-unfilled">No Critical or High findings.</p>
          ) : (
            <ol className="dr-ol">
              {generated.key_findings.map((k, i) => (
                <li key={i}>
                  <strong>{k.title}</strong>
                  {/* Some findings carry no description — don't leave a dangling
                      semicolon behind the title. */}
                  {k.description ? <> — {k.description}</> : null}
                </li>
              ))}
            </ol>
          )}
        </Section>

        <Section id="dr-root" title="Root Cause Analysis">
          <RootCauseDonut themes={generated.root_cause_themes} />
        </Section>

        <Section id="dr-summary" title="Summary of Audit Findings">
          <SummaryTable
            row={generated.summary_row}
            ratingLabel={generated.audit_rating.label}
            ratingClass={generated.audit_rating.css_class}
          />
        </Section>

        <Section id="dr-objectives" title="The Objectives" editable>
          <textarea
            className="dr-input dr-textarea"
            rows={4}
            placeholder="What this review set out to determine…"
            value={ed.objectives}
            onChange={(e) => patch((d) => ({ ...d, objectives: e.target.value }))}
          />
        </Section>

        <Section id="dr-scope" title="The Scope" editable>
          <ListEditor
            items={ed.scope}
            placeholder="Add a scope item…"
            suggestions={generated.scope_suggestions}
            onChange={(scope) => patch((d) => ({ ...d, scope }))}
          />
        </Section>

        <Section id="dr-team" title="Audit Team" editable>
          <label className="dr-label" htmlFor="dr-em">Engagement Manager</label>
          <input
            id="dr-em"
            className="dr-input"
            placeholder="Name"
            value={ed.audit_team.engagement_manager}
            onChange={(e) =>
              patch((d) => ({
                ...d,
                audit_team: { ...d.audit_team, engagement_manager: e.target.value },
              }))
            }
          />
          <label className="dr-label">Team Members</label>
          <ListEditor
            items={ed.audit_team.team_members}
            placeholder="Add a team member…"
            onChange={(team_members) =>
              patch((d) => ({ ...d, audit_team: { ...d.audit_team, team_members } }))
            }
          />
        </Section>

        <Section id="dr-detailed" title="Detailed Audit Report">
          <div className="dr-findings">
            {generated.findings.map((f, i) => (
              <FindingTable
                key={f.source_fid || i}
                index={i + 1}
                finding={f}
                edits={ed.findings[f.source_fid]}
                onChange={(row) =>
                  patch((d) => ({
                    ...d,
                    findings: { ...d.findings, [f.source_fid]: row },
                  }))
                }
              />
            ))}
          </div>
        </Section>

        <Section id="dr-appendix-a" title="Appendix A — Risk Rating Definitions">
          <div className="table-wrap">
            <table className="dr-table">
              <thead>
                <tr>
                  <th>Observation&apos;s Rating</th>
                  <th>Indicative time frame for corrective action</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {APPENDIX_A_ROWS.map((r) => (
                  <tr key={r.rating}>
                    <td><strong>{r.rating}</strong></td>
                    <td>{r.timeframe}</td>
                    <td>{r.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section id="dr-appendix-b" title="Appendix B — Audit Report Rating Matrix">
          <div className="table-wrap">
            <table className="dr-table">
              <thead>
                <tr>
                  <th>Overall Rating</th>
                  <th>High</th>
                  <th>Medium</th>
                </tr>
              </thead>
              <tbody>
                {APPENDIX_B_ROWS.map((r) => (
                  <tr
                    key={r.rating}
                    className={
                      r.rating === generated.audit_rating.label ? 'dr-row-active' : ''
                    }
                  >
                    <td><strong>{r.rating}</strong></td>
                    <td>{r.high}</td>
                    <td>{r.medium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="dr-notes-title">Notes</p>
          <ol className="dr-ol dr-notes">
            {APPENDIX_B_NOTES.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ol>
        </Section>
      </div>
    </div>
  );
}

function SummaryTable({
  row,
  ratingLabel,
  ratingClass,
}: {
  row: DraftGenerated['summary_row'];
  ratingLabel: string;
  ratingClass: string;
}): JSX.Element {
  const cell = (n: number) => (n ? String(n) : '–');
  /* Colour-coded exactly like the PDF (templates/oia_audit_report.css): the severity
     columns carry the severity's own colour, and the Report Rating row is filled with
     the band colour. Same palette in both places — the panel and the download must not
     disagree about what "Unacceptable" looks like. */
  return (
    <div className="table-wrap">
      <table className="dr-table dr-summary">
        <thead>
          <tr>
            <th className="sf-title" colSpan={6}>Summary of Audit Findings</th>
          </tr>
          <tr>
            <th className="sf-first">Finding Criticality</th>
            <th className="sf-high">High</th>
            <th className="sf-medium">Medium</th>
            <th className="sf-low">Low</th>
            <th className="sf-rec">Recommendations</th>
            <th className="sf-first">Total Findings</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="sf-first"><strong>No. of Findings</strong></td>
            <td>{cell(row.high)}</td>
            <td>{cell(row.medium)}</td>
            <td>{cell(row.low)}</td>
            <td>{cell(row.recommendation)}</td>
            <td><strong>{row.total}</strong></td>
          </tr>
          <tr className={`dr-rating-row rating-${ratingClass}`}>
            <td className="sf-first"><strong>Report Rating</strong></td>
            <td colSpan={5}>{ratingLabel}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** One finding, as the PDF lays it out: a label/value table. The three analyst
 *  rows are inputs; the rest is AI-derived and read-only. */
function FindingTable({
  index,
  finding,
  edits,
  onChange,
}: {
  index: number;
  finding: DraftFinding;
  edits?: { mgmt_action_plan: string; responsibility: string; target_date: string };
  onChange: (row: {
    mgmt_action_plan: string;
    responsibility: string;
    target_date: string;
  }) => void;
}): JSX.Element {
  const row = edits ?? {
    mgmt_action_plan: '',
    responsibility: '',
    target_date: '',
  };
  const set = (k: keyof typeof row, v: string) => onChange({ ...row, [k]: v });

  return (
    <article className="dr-finding">
      <header className="dr-finding-head">
        <span className="t-mono t-xs">{index}.</span>
        <span className="dr-finding-title">{finding.title}</span>
        {finding.source_fid && (
          /* A label, NOT a link. It used to anchor to the finding's card in the
             Detailed AI Report — but that panel is collapsed by default now, so its
             sections are not in the DOM and the jump had nowhere to land. A link that
             does nothing is worse than no link. The id still tells the reader which
             §11 finding this is; the tooltip says where to find it. */
          <span
            className="badge badge-neutral t-xs"
            style={{ fontFamily: 'var(--font-mono)' }}
            data-tip={`${finding.source_fid} — Section 11 of the Detailed AI Report`}
          >
            {finding.source_fid}
          </span>
        )}
        <span className={`badge dr-chip-${finding.rating_class}`}>
          {finding.rating_label}
        </span>
      </header>

      <div className="table-wrap">
        <table className="dr-table dr-finding-table">
          <tbody>
            <tr>
              <th>Observation</th>
              <td><Prose html={finding.observation_html} /></td>
            </tr>
            <tr>
              <th>Risk / Impact</th>
              <td><Prose html={finding.risk_impact_html} /></td>
            </tr>
            <tr>
              <th>Root Cause</th>
              <td><Prose html={finding.root_cause_html} /></td>
            </tr>
            <tr>
              <th>Recommendation</th>
              <td><Prose html={finding.recommendation_html} /></td>
            </tr>
            <tr>
              <th>
                Management Action Plan <EditableMark />
              </th>
              <td>
                <textarea
                  className="dr-input dr-textarea"
                  rows={3}
                  placeholder="What management commits to doing…"
                  value={row.mgmt_action_plan}
                  onChange={(e) => set('mgmt_action_plan', e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <th>Responsibility</th>
              <td>
                <input
                  className="dr-input"
                  placeholder="Owner"
                  value={row.responsibility}
                  onChange={(e) => set('responsibility', e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <th>Target Date</th>
              <td>
                {/* A real date picker. Free text invited "Q3", "end of Sept", "30/9/26"
                    — three analysts, three formats, and nothing sortable or comparable.
                    The value is stored ISO (2026-09-30); the report formats it as
                    "30 September 2026" when it prints. */}
                <input
                  type="date"
                  className="dr-input dr-date"
                  value={row.target_date}
                  onChange={(e) => set('target_date', e.target.value)}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  );
}

/** Edit a list of lines. `suggestions` are offered only while the list is empty —
 *  the standard scope wording, one click away, without printing it by default. */
function ListEditor({
  items,
  placeholder,
  suggestions,
  onChange,
}: {
  items: string[];
  placeholder: string;
  suggestions?: string[];
  onChange: (items: string[]) => void;
}): JSX.Element {
  const [draft, setDraft] = useState('');

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...items, v]);
    setDraft('');
  };

  return (
    <div className="dr-list">
      {items.map((it, i) => (
        <div key={i} className="dr-list-row">
          <input
            className="dr-input"
            value={it}
            onChange={(e) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
          />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            aria-label={`Remove "${it}"`}
            data-tip="Remove"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
          >
            ✕
          </button>
        </div>
      ))}

      <div className="dr-list-row">
        <input
          className="dr-input"
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={!draft.trim()}
          onClick={add}
        >
          Add
        </button>
      </div>

      {items.length === 0 && suggestions && suggestions.length > 0 && (
        <div className="dr-suggest">
          <span className="dr-suggest-label">Standard scope:</span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => onChange([...suggestions])}
          >
            Insert all {suggestions.length}
          </button>
        </div>
      )}
    </div>
  );
}
